import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { resetShortcuts, setModalOpen, setShortcuts } from 'redux/shortcuts/reducer';
import { useSelector } from 'react-redux';
import { selectIsModalOpen, selectShortcuts } from 'redux/shortcuts/selectors';
import { useAppSelector } from 'redux/hooks';
import { ReactComponent as CloseIcon } from "assets/icons/new/UI/CloseCircleFilled.svg";
import { Shortcut } from 'types/shortcut';

type TShortcutComponent = {
  shortcut: Shortcut;
  saveEdit: (newValue: string, command: string) => void
}
const ShortcutComponent = ({shortcut, saveEdit}: TShortcutComponent) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(shortcut.key);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveEdit(editValue, shortcut.command);
      setIsEditing(false);
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  const saveEditFn = () => {
    saveEdit(editValue, shortcut.command)
    setIsEditing(false);
  }

  return (
    <li key={shortcut.command} className={`flex group justify-between items-center gap-x-2 py-2.5 border-t border-theme-border dark:border-theme-border`}>
      <span className={`hover:text-theme-hover dark:hover:text-theme-hover text-sm`}>{shortcut.description}</span>
      <div className="flex items-center gap-x-2">
        {isEditing ? (
          <div className="flex items-center gap-x-1">
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value.toUpperCase())}
              onKeyPress={handleKeyPress}
              className="w-8 text-center rounded-md py-1 px-1 text-sm"
              maxLength={1}
              autoFocus
            />
            <button
              onClick={saveEditFn}
              className="p-1 text-green-600 hover:text-green-800"
            >
              ✓
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="p-1 text-red-600 hover:text-red-800"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-x-1">
            <div className="flex gap-x-1">
              <span className={`min-h-7.5 inline-flex justify-center items-center py-1 px-1.5 bg-white border border-theme-border dark:border-theme-border font-mono text-sm rounded-md`}>
                ⌘
              </span>
              <span className={`min-h-7.5 inline-flex justify-center items-center py-1 px-1.5 bg-white border border-theme-border dark:border-theme-border font-mono text-sm  rounded-md`}>
                {shortcut.key}
              </span>
            </div>
            <button
              onClick={() => setIsEditing(true)}
              className="p-1 opacity-0 hover:opacity-100 group-hover:opacity-100 transition-opacity"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </li>
  )
}

const KeyboardShortcutsModal: React.FC<{ saveShortcuts: (newShortcuts: Shortcut[]) => void}> = ({ saveShortcuts }) => {
  const dispatch = useDispatch();
  const shortcuts = useSelector(selectShortcuts);
  const [newShortcuts, setNewShortcuts] = useState(shortcuts);
  
  const saveEdit = useCallback(() => {
    dispatch(setShortcuts(newShortcuts));
    saveShortcuts(newShortcuts)
    handleClose()
  }, [dispatch, newShortcuts]);

  const editingFn = useCallback((newValue: string, command: string) => {
    if (newValue.length === 1) {
      const key = newValue.toUpperCase();
      
      // Проверка на дубликаты
      const isDuplicate = newShortcuts.some(
        s => s.key === key
      );

      if (isDuplicate) {
        alert('Эта клавиша уже используется!');
        return;
      }

      setNewShortcuts(old => old.map(shortcut =>
        shortcut.command === command
          ? { ...shortcut, key }
          : shortcut
      ))
    }
  }, [dispatch, newShortcuts]);
  const isOpen = useSelector(selectIsModalOpen);
  const onClose = () => dispatch(setModalOpen(false))

  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');


  // Фильтрация горячих клавиш по поиску
  const filteredShortcuts = newShortcuts.filter(shortcut =>
    shortcut.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    shortcut.key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (
      modalRef.current && 
      !modalRef.current.contains(e.target as Node) &&
      overlayRef.current &&
      overlayRef.current.contains(e.target as Node)
    ) {
      onClose();
      dispatch(setModalOpen(false));
    }
  }, [onClose, dispatch]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('mousedown', handleClickOutside);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleClickOutside]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        dispatch(setModalOpen(false));
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, dispatch]);

  const handleClose = () => {
    onClose();
    dispatch(setModalOpen(false));
  };

  const handleReset = () => {
    dispatch(resetShortcuts());
  };

  return (
    <div 
      ref={overlayRef}
      className={`hs-overlay size-full fixed top-0 start-0 overflow-x-hidden overflow-y-auto bg-theme-bg dark:bg-theme-bg bg-opacity-60 ${isOpen ? 'z-[99] opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      onClick={(e) => {
        if (e.target === overlayRef.current) {
          handleClose();
        }
      }}
    >
      <div className={`hs-overlay-open:mt-7 hs-overlay-open:opacity-100 hs-overlay-open:duration-500 mt-0 ${isOpen ? 'z-[99] opacity-100' : 'opacity-0'} ease-out transition-all sm:max-w-xl sm:w-full m-3 sm:mx-auto h-[calc(100%-56px)] min-h-[calc(100%-56px)] flex items-center z-[100]`}>
        <div 
          ref={modalRef}
          className={`w-full max-h-full flex flex-col shadow-2xl border border-theme-border dark:border-theme-border bg-theme-bg dark:bg-theme-bg rounded-xl pointer-events-auto z-[101]`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Заголовок и кнопка закрытия */}
          <div className={`py-4 px-6 flex justify-between items-center border-b border-theme-border dark:border-theme-border`}>
            <h3 className="font-medium">
              Горячие клавиши
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={handleReset}
                className={`py-1 px-3 text-sm hover:text-theme-hover dark:hover:text-theme-hover`}
              >
                Сбросить
              </button>
              <button
                type="button"
                className={`size-8 shrink-0 flex justify-center items-center gap-x-2 rounded-full hover:text-theme-hover dark:hover:text-theme-hover`}
                aria-label="Close"
                onClick={handleClose}
              >
                <span className="sr-only">Close</span>
                <CloseIcon width="24" height="24" stroke="currentColor" className="shrink-0"/>
              </button>
            </div>
          </div>

          {/* Поиск */}
          <div className={`py-2 px-4 border-b border-theme-border dark:border-theme-border`}>
            <div className="relative">
              <div className="absolute inset-y-0 start-0 flex items-center pointer-events-none z-20">
                <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.3-4.3"/>
                </svg>
              </div>
              <input 
                type="text" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="py-1.5 sm:py-2 ps-7 pe-20 block w-full bg-transparent border-transparent rounded-lg sm:text-sm focus:z-10 focus:outline-hidden focus:border-transparent focus:ring-0" 
                placeholder="Поиск горячих клавиш"
              />
              <div className="absolute inset-y-0 end-0 flex items-center pointer-events-none z-20">
                <span className={`min-h-7.5 inline-flex justify-center items-center py-0.5 px-1.5 bg-white font-mono text-sm text-gray-600 rounded-md border border-theme-border dark:border-theme-border`}>
                  ⌘ + L
                </span>
              </div>
            </div>
          </div>

          {/* Список горячих клавиш */}
          <div className="h-96 overflow-y-auto">
            <div className="p-4 space-y-5">
              <div>
                <h4 className="font-semibold mb-2.5">
                  Основные команды
                </h4>

                <ul>
                  {filteredShortcuts.map((shortcut) => (
                    <ShortcutComponent
                      key={shortcut.command}
                      shortcut={shortcut}
                      saveEdit={editingFn}
                    />
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Футер с кнопкой сохранения */}
          <div className={`py-3 px-6 border-t border-theme-border dark:border-theme-border`}>
            <div className="flex justify-end">
              <button
                onClick={saveEdit}
                className={`hover:text-theme-hover dark:hover:text-theme-hover py-2 px-4 text-sm font-medium bg-white border border-theme-border dark:border-theme-border rounded-lg`}
              >
                Готово
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsModal;